import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

type Role = "buyer" | "seller" | "driver";

interface ActiveOrder {
  orderId: string;
  deliveryId?: string;
  role: Role;
  status: string;
  counterpartyName: string;
  counterpartyImageUrl?: string;
  etaIso?: string;
  currentStepLabel?: string;
  scheduledFor?: string;
  tapTarget?: string;
  updatedAt?: string;
}

// A driver's accepted delivery shouldn't squat at the top of the screen for
// days. It stays in "My Deliveries" until ~24h before the scheduled pickup,
// then shows a snoozable heads-up ("got it, see you then"), and finally a
// non-dismissible bar from the morning of through completion.
type DeliveryPhase = "hidden" | "headsup" | "active";

const SNOOZE_PREFIX = "stoop.delivery-snooze.";
function isSnoozed(orderId: string): boolean {
  try {
    return localStorage.getItem(SNOOZE_PREFIX + orderId) === "1";
  } catch {
    return false;
  }
}
function snooze(orderId: string): void {
  try {
    localStorage.setItem(SNOOZE_PREFIX + orderId, "1");
  } catch {
    /* private mode — snooze just won't persist */
  }
}

function deliveryPhase(scheduledFor: string | undefined, snoozed: boolean): DeliveryPhase {
  if (!scheduledFor) return "active";
  const scheduledMs = new Date(scheduledFor).getTime();
  if (Number.isNaN(scheduledMs)) return "active";
  const now = Date.now();
  const dayOf = new Date(scheduledFor);
  dayOf.setHours(0, 0, 0, 0);
  if (now >= dayOf.getTime()) return "active";
  if (now >= scheduledMs - 24 * 60 * 60 * 1000) return snoozed ? "hidden" : "headsup";
  return "hidden";
}

function formatPickupWindow(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ActiveOrdersResponse {
  activeOrders: ActiveOrder[];
}

const ROLE_PRIORITY: Record<Role, number> = { driver: 0, seller: 1, buyer: 2 };

function buyerStatusVerb(status: string): string {
  switch (status) {
    case "paid":
      return "Order placed";
    case "preparing":
      return "Being prepared";
    case "ready_for_pickup":
      return "Ready for pickup";
    case "out_for_delivery":
      return "Out for delivery";
    default:
      return "In progress";
  }
}

function sellerStatusVerb(status: string): string {
  switch (status) {
    case "paid":
      return "New order";
    case "preparing":
      return "Preparing";
    case "ready_for_pickup":
      return "Ready for pickup";
    case "out_for_delivery":
      return "Out for delivery";
    default:
      return "In progress";
  }
}

function sellerActionHint(status: string): string {
  switch (status) {
    case "paid":
      return "Start preparing when you're ready.";
    case "preparing":
      return "Mark ready when prepared.";
    case "ready_for_pickup":
      return "Waiting on pickup.";
    case "out_for_delivery":
      return "On its way to the buyer.";
    default:
      return "";
  }
}

function formatEta(etaIso?: string): string | null {
  if (!etaIso) return null;
  const t = new Date(etaIso);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function pickPrimary(orders: ActiveOrder[]): { primary: ActiveOrder; extraCount: number } | null {
  if (orders.length === 0) return null;

  const byRole = [...orders].sort((a, b) => ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role]);
  const topRole = byRole[0].role;
  const sameRole = byRole.filter((o) => o.role === topRole);

  const mostRecent = sameRole.reduce((best, cur) => {
    const bestTime = best.updatedAt ? new Date(best.updatedAt).getTime() : 0;
    const curTime = cur.updatedAt ? new Date(cur.updatedAt).getTime() : 0;
    return curTime > bestTime ? cur : best;
  }, sameRole[0]);

  return { primary: mostRecent, extraCount: sameRole.length - 1 };
}

function buildLabel(order: ActiveOrder, phase: DeliveryPhase): string {
  if (order.role === "buyer") {
    const verb = buyerStatusVerb(order.status);
    const eta = formatEta(order.etaIso);
    const etaSuffix = eta ? ` ETA ${eta}.` : "";
    return `Your order from ${order.counterpartyName} — ${verb}.${etaSuffix}`;
  }
  if (order.role === "seller") {
    const firstName = order.counterpartyName.split(" ")[0] || order.counterpartyName;
    const verb = sellerStatusVerb(order.status);
    const hint = sellerActionHint(order.status);
    return `${firstName}'s order — ${verb}.${hint ? ` ${hint}` : ""}`;
  }
  const window = formatPickupWindow(order.scheduledFor);
  if (phase === "headsup") {
    return `Delivery in ~24h${window ? ` — ${window}` : ""}. Tap to review.`;
  }
  if (order.status === "in_progress") {
    return `Delivery in progress — ${order.currentStepLabel ?? "en route"}.`;
  }
  return `Delivery today${window ? ` — ${window}` : ""}. Pick up when the seller's ready.`;
}

function tapTarget(order: ActiveOrder): string {
  if (order.tapTarget) return order.tapTarget;
  if (order.role === "driver") return "/deliveries?tab=my";
  return `/orders/${order.orderId}`;
}

export default function ActiveOrderBanner() {
  const [, setLocation] = useLocation();
  const { isAuthenticated } = useAuth();
  const [, forceRerender] = useState(0);

  const { data } = useQuery<ActiveOrdersResponse>({
    queryKey: ["/api/my/active-orders"],
    enabled: isAuthenticated,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Driver deliveries scheduled further out (or snoozed before the day-of)
  // are kept out of the bar; they still live in "My Deliveries".
  const orders = (data?.activeOrders ?? []).filter((o) =>
    o.role === "driver"
      ? deliveryPhase(o.scheduledFor, isSnoozed(o.orderId)) !== "hidden"
      : true,
  );
  const picked = pickPrimary(orders);
  if (!picked) return null;

  const { primary } = picked;
  const phase: DeliveryPhase =
    primary.role === "driver" ? deliveryPhase(primary.scheduledFor, isSnoozed(primary.orderId)) : "active";
  const canSnooze = primary.role === "driver" && phase === "headsup";
  const totalCount = orders.length;
  const extraCount = totalCount - 1;
  const label = buildLabel(primary, phase);
  // One active item → jump straight to it. Multiple → open the list so the
  // user can see all of them (per feedback: the bar must open a real list).
  const href = totalCount > 1 ? "/active" : tapTarget(primary);

  return (
    <div className="border-b border-primary/20 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
      <div className="container mx-auto px-4">
        <div className="flex w-full items-center gap-3 py-3" data-testid="active-order-banner">
          <button
            type="button"
            onClick={() => setLocation(href)}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-foreground/60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" data-testid="active-order-banner-label">
                {label}
              </p>
              {extraCount > 0 && (
                <p className="text-xs opacity-80">+{extraCount} more</p>
              )}
            </div>
            {!canSnooze && <ChevronRight className="h-5 w-5 shrink-0 opacity-80" />}
          </button>
          {canSnooze && (
            <button
              type="button"
              onClick={() => {
                snooze(primary.orderId);
                forceRerender((n) => n + 1);
              }}
              className="shrink-0 rounded-full bg-primary-foreground/20 px-3 py-1 text-xs font-semibold"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
